/**
 * Shared submit-button gate for both the Create and Edit order dialogs.
 *
 * Lives outside React so we can unit-test every branch and run the same
 * fixtures through both dialog code paths. If you find yourself adding a
 * private validation gate inside one of the dialogs, add it here instead.
 */
import type { OrderFormData } from "@/components/orders/orderFormTypes";
import { formLocalToUtc } from "@/lib/timezone";
import {
  hasConfirmTrailerType,
  hasConfirmTripType,
  isPerLegAssets,
  isPerLegTripType,
} from "@/lib/order-form-assets";
import { resolveFormLegs } from "@/lib/order-form-legs";

export interface OrderFormValidationOptions {
  /** True when validating an edit of an existing order. Past-time defaults
   *  are enforced for new orders on all time fields, and for edits only on
   *  time fields the user actually changed (so an existing order whose ready
   *  window has already elapsed can still be edited elsewhere without tripping
   *  the past-time guard). */
  isEditing: boolean;
  /** Original form data captured when the edit dialog hydrated. Required to
   *  scope the past-time check to fields that actually changed on edit. */
  originalFormData?: OrderFormData;
  /** Override for `Date.now()` — primarily for tests. */
  nowMs?: number;
  /** Historical / billing-only edit by an admin. When true the past-time
   *  guard is bypassed entirely so managers can correct delivered or
   *  canceled orders for billing/audit purposes without re-keying every
   *  time field. Window-order checks (`end >= start`) still apply. The
   *  matching write-path gate in `useOrderMutations` skips all external
   *  (EAPI / pivot) side effects when `_historicalEdit` is on the payload. */
  historicalEdit?: boolean;
}

export interface OrderFormValidationResult {
  valid: boolean;
  /** Human-readable reasons. Empty when `valid` is true. UI may surface the
   *  first reason as a tooltip or inline message. */
  reasons: string[];
}

/** Resolve a form value (`YYYY-MM-DDTHH:mm`, Central) to an epoch ms instant. */
function localToMs(local: string | undefined | null): number | null {
  if (!local || !local.includes("T")) return null;
  const iso = formLocalToUtc(local);
  if (!iso) return null;
  const ts = new Date(iso).getTime();
  return Number.isFinite(ts) ? ts : null;
}

function isLocalInPast(local: string, nowMs: number): boolean {
  const ts = localToMs(local);
  return ts !== null && ts < nowMs;
}


export function isOrderFormValid(
  fd: OrderFormData,
  opts: OrderFormValidationOptions,
): OrderFormValidationResult {
  const reasons: string[] = [];
  const nowMs = opts.nowMs ?? Date.now();

  if (!fd.billing_party_id) reasons.push("Billing party is required.");

  // Stop-level rules. Every order is read as a list of legs — a single-leg
  // order is simply a list of one built from the order-level fields — so
  // both shapes go through exactly the same checks.
  const isMulti = fd.order_type === "multi-leg";
  if (isMulti && (!fd.legs || fd.legs.length < 2)) {
    reasons.push("Multi-leg orders require at least 2 stops.");
  }
  resolveFormLegs(fd).forEach((stop) => {
    const p = isMulti ? `Leg ${stop.index + 1}: ` : "";
    if (!stop.shipFromStreetAddress.trim() || !stop.shipFromCity.trim()) {
      reasons.push(`${p}Ship From address is required.`);
    }
    if (!stop.shipToStreetAddress.trim() || !stop.shipToCity.trim()) {
      reasons.push(`${p}Ship To address is required.`);
    }
    if (
      !stop.readyTime ||
      !stop.deliveryTime ||
      !stop.readyTimeEndRaw ||
      !stop.deliveryTimeEndRaw
    ) {
      reasons.push(`${p}All time windows are required.`);
    }
    if (stop.readyTime && stop.readyTimeEndRaw && stop.readyTimeEndRaw < stop.readyTime) {
      reasons.push(`${p}Ready window end must be after start.`);
    }
    if (
      stop.deliveryTime &&
      stop.deliveryTimeEndRaw &&
      stop.deliveryTimeEndRaw < stop.deliveryTime
    ) {
      reasons.push(`${p}Delivery window end must be after start.`);
    }
  });
  // Trip type is only required to *confirm* an order (handled below), so a
  // booked multi-leg order may be created before per-leg trip types are known
  // — same rule the single-leg form already applies.


  if (fd.is_recurring && fd.recurrence_days.length === 0) {
    reasons.push("Recurring orders require at least one day.");
  }

  // Confirmed-status promotion gate. Required assets (tractor, trailer type)
  // and trip type must be present before an order can be saved as
  // `confirmed`. For multi-leg orders these fields may live on each leg when
  // the "same assets" / "same trip type" toggles are off — validate the
  // right level so we don't block a save just because the parent mirrors
  // are unset by design.
  if (fd.status === "confirmed") {
    // Tractor + trailer presence (bobtail exception) is enforced by the
    // dialogs' explicit confirm modals via `hasConfirmAssets`, which needs
    // trailer_types data this pure helper doesn't have. Here we only gate
    // on data the validator can see: trailer type and trip type presence.
    if (!hasConfirmTrailerType(fd, undefined)) {
      reasons.push(
        isPerLegAssets(fd)
          ? "Each leg requires a trailer type to confirm."
          : "Trailer type is required to confirm.",
      );
    }
    if (!hasConfirmTripType(fd)) {
      reasons.push(
        isPerLegTripType(fd)
          ? "Each leg requires a trip type to confirm."
          : "Trip type is required to confirm.",
      );
    }
  }

  // Past-time guard. For new orders, every time field must be in the future.
  // For edits, only times the dispatcher actually moved are checked:
  //  - unchanged instants (even when the string was reformatted or
  //    re-expressed after a timezone/address change) are skipped;
  //  - an already-past value that was not pushed further into the past is
  //    skipped, so in-flight orders stay editable elsewhere.
  // Legs are matched by `id`, not array position, so adding/removing a leg
  // does not make untouched legs look changed.
  // Historical/billing-only edits bypass this entirely — see option doc.
  if (!fd.is_recurring && !opts.historicalEdit) {
    const orig = opts.originalFormData;
    const isChangedPast = (next: string | undefined, prev: string | undefined): boolean => {
      if (!next) return false;
      if (opts.isEditing && prev) {
        const nextMs = localToMs(next);
        const prevMs = localToMs(prev);
        // Same instant → untouched.
        if (nextMs !== null && prevMs !== null && nextMs === prevMs) return false;
        // Original already in the past and the edit doesn't move it earlier.
        if (
          nextMs !== null &&
          prevMs !== null &&
          prevMs < nowMs &&
          nextMs >= prevMs
        ) {
          return false;
        }
        if (next === prev) return false;
      }
      return isLocalInPast(next, nowMs);
    };

    let anyPast = false;
    if (fd.order_type === "multi-leg") {
      const origLegs = orig?.legs ?? [];
      const origById = new Map<string, (typeof origLegs)[number]>();
      origLegs.forEach((l) => {
        const id = (l as { id?: string }).id;
        if (id) origById.set(id, l);
      });
      anyPast = (fd.legs ?? []).some((l, i) => {
        const id = (l as { id?: string }).id;
        // New legs (no id) have no original counterpart — validate strictly.
        const o = id ? origById.get(id) : origById.size ? undefined : origLegs[i];
        return (
          isChangedPast(l.ready_time, o?.ready_time) ||
          isChangedPast(l.ready_time_end, o?.ready_time_end) ||
          isChangedPast(l.requested_delivery_time, o?.requested_delivery_time) ||
          isChangedPast(l.requested_delivery_time_end, o?.requested_delivery_time_end)
        );
      });
    } else {
      anyPast =
        isChangedPast(fd.ready_time, orig?.ready_time) ||
        isChangedPast(fd.ready_time_end, orig?.ready_time_end) ||
        isChangedPast(fd.requested_delivery_time, orig?.requested_delivery_time) ||
        isChangedPast(fd.requested_delivery_time_end, orig?.requested_delivery_time_end);
    }

    if (anyPast) {
      reasons.push("Times cannot be in the past.");
    }
  }

  return { valid: reasons.length === 0, reasons };
}